import { useState, useEffect, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { Loader2, MapPin, Route, Clock, Play, Check, Phone, Navigation } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const TYPE_COLOR = { awaria: "#EF4444", montaz: "#06B6D4", prace: "#F59E0B" };
const STATUS_LABEL = { zaplanowane: "Zaplanowane", podjete: "Zajęty (w toku)", gotowe: "Gotowe" };

function hav(a, b) {
  const R = 6371, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
function nearestNeighbor(pts) {
  const n = pts.length;
  if (n <= 2) return pts.map((_, i) => i);
  const used = Array(n).fill(false), order = [0];
  used[0] = true;
  for (let k = 1; k < n; k++) {
    const last = order[order.length - 1];
    let best = -1, bd = Infinity;
    for (let j = 0; j < n; j++) {
      if (used[j]) continue;
      const d = hav(pts[last], pts[j]);
      if (d < bd) { bd = d; best = j; }
    }
    order.push(best); used[best] = true;
  }
  return order;
}
const numIcon = (n, color) => L.divIcon({
  className: "", iconSize: [28, 28], iconAnchor: [14, 14],
  html: `<div style="background:${color};width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:13px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)">${n}</div>`,
});

function FitBounds({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords.length === 1) map.setView(coords[0], 14);
    else if (coords.length > 1) map.fitBounds(coords, { padding: [40, 40] });
  }, [coords, map]);
  return null;
}

export default function ScheduleToday({ role, user, isManager, monters, filterMonter, onReload, onLocate }) {
  const { t } = useI18n();
  const [date, setDate] = useState(fmtDate(new Date()));
  const [data, setData] = useState({ tasks: [] });
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("route"); // route | time
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      if (isManager && filterMonter !== "all") params.assignee_id = filterMonter;
      const { data: d } = await api.get("/schedule/today", { params });
      setData(d);
    } finally { setLoading(false); }
  }, [date, isManager, filterMonter]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (id, action) => {
    setBusy(true);
    try {
      await api.post(`/schedule/tasks/${id}/${action}`);
      toast.success(action === "accept" ? t("Podjęto zadanie") : t("Zadanie zakończone"));
      load(); onReload && onReload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const { ordered, geoCoords, noGeo, totalKm } = useMemo(() => {
    const tasks = data.tasks || [];
    const withGeo = tasks.filter((tk) => tk.lat != null && tk.lng != null);
    const without = tasks.filter((tk) => tk.lat == null || tk.lng == null);
    const pts = withGeo.map((tk) => [tk.lat, tk.lng]);
    let idx = withGeo.map((_, i) => i);
    if (mode === "route" && pts.length > 1) idx = nearestNeighbor(pts);
    const ord = idx.map((i) => withGeo[i]);
    const coords = idx.map((i) => pts[i]);
    let km = 0;
    for (let i = 1; i < coords.length; i++) km += hav(coords[i - 1], coords[i]);
    return { ordered: ord, geoCoords: coords, noGeo: without, totalKm: km };
  }, [data, mode]);

  const input = "bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-cyan-500";

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-full" data-testid="schedule-today">
      {/* Left: ordered list */}
      <div className="lg:w-[420px] shrink-0 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="today-date" className={input} />
          <div className="flex rounded-md overflow-hidden border border-slate-700">
            <button onClick={() => setMode("route")} data-testid="today-mode-route"
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${mode === "route" ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}><Route size={13} /> {t("Wg trasy")}</button>
            <button onClick={() => setMode("time")} data-testid="today-mode-time"
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${mode === "time" ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}><Clock size={13} /> {t("Wg godziny")}</button>
          </div>
        </div>
        <div className="text-xs text-slate-400 mb-2 flex items-center gap-3">
          <span>{t("Zleceń")}: <b className="text-slate-200">{(data.tasks || []).length}</b></span>
          {mode === "route" && geoCoords.length > 1 && <span className="flex items-center gap-1"><Navigation size={12} className="text-cyan-400" /> {t("Trasa")}: <b className="text-cyan-300">{totalKm.toFixed(1)} km</b></span>}
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="animate-spin mr-2" /> {t("Ładowanie…")}</div>
          ) : (data.tasks || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">{t("Brak zleceń na ten dzień.")}</div>
          ) : (
            <>
              {ordered.map((tk, i) => {
                const color = TYPE_COLOR[tk.type] || "#06B6D4";
                const canAct = isManager || (role === "monter" && tk.assignee_id === user?.id);
                return (
                  <div key={tk.id} data-testid={`today-task-${tk.id}`} className="bg-slate-800 border border-slate-700 rounded-lg p-3">
                    <div className="flex items-start gap-2.5">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5" style={{ background: color }}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold truncate">{tk.title}</span>
                          <span className="text-[10px] text-slate-400">{tk.scheduled_time}</span>
                        </div>
                        <div className="text-xs text-slate-400 truncate">{tk.type_label} · {tk.assignee_name || t("nieprzypisane")} · {t(STATUS_LABEL[tk.status] || tk.status)}</div>
                        {tk.client_brief && (
                          <div className="text-xs text-slate-300 mt-1 space-y-0.5">
                            <div className="flex items-center gap-1"><MapPin size={11} className="text-cyan-400" /> {tk.client_brief.address || "—"}</div>
                            {tk.client_brief.phone && <div className="flex items-center gap-1"><Phone size={11} className="text-cyan-400" /> <a href={`tel:${tk.client_brief.phone}`} className="text-cyan-300">{tk.client_brief.phone}</a></div>}
                            <div className="text-slate-500">{t("Splitter")} {tk.client_brief.splitter_number || "—"} · {t("port")} {tk.client_brief.splitter_port ?? "—"} · {t("Moc")} {tk.client_brief.measured_rx_dbm ?? "?"}/{tk.client_brief.expected_rx_dbm ?? "?"} dBm</div>
                          </div>
                        )}
                        {!tk.client_brief && tk.address && <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin size={11} /> {tk.address}</div>}
                        {canAct && (
                          <div className="flex items-center gap-2 mt-2">
                            {tk.status === "zaplanowane" && tk.assignee_id && (
                              <button onClick={() => doAction(tk.id, "accept")} disabled={busy} data-testid={`today-accept-${tk.id}`} className="text-xs bg-yellow-600 hover:bg-yellow-500 rounded px-2.5 py-1 flex items-center gap-1 disabled:opacity-60"><Play size={12} /> {t("Podejmij")}</button>
                            )}
                            {tk.status === "podjete" && (
                              <button onClick={() => doAction(tk.id, "complete")} disabled={busy} data-testid={`today-complete-${tk.id}`} className="text-xs bg-green-600 hover:bg-green-500 rounded px-2.5 py-1 flex items-center gap-1 disabled:opacity-60"><Check size={12} /> {t("Gotowe")}</button>
                            )}
                            {tk.lat != null && onLocate && (
                              <button onClick={() => onLocate({ lat: tk.lat, lng: tk.lng })} className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 flex items-center gap-1"><MapPin size={12} /> {t("Mapa")}</button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {noGeo.length > 0 && (
                <div className="text-[11px] text-slate-500 border-t border-slate-800 pt-2 mt-2">
                  {t("Bez lokalizacji (nie na trasie)")}: {noGeo.map((tk) => tk.title).join(", ")}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Right: route map */}
      <div className="flex-1 min-h-[360px] rounded-lg overflow-hidden border border-slate-700" data-testid="today-map">
        {geoCoords.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-500 bg-slate-800/40">{t("Brak zleceń z lokalizacją do pokazania na mapie.")}</div>
        ) : (
          <MapContainer center={geoCoords[0]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            <FitBounds coords={geoCoords} />
            {mode === "route" && geoCoords.length > 1 && <Polyline positions={geoCoords} pathOptions={{ color: "#06B6D4", weight: 3, dashArray: "6 6" }} />}
            {ordered.map((tk, i) => (
              <Marker key={tk.id} position={[tk.lat, tk.lng]} icon={numIcon(i + 1, TYPE_COLOR[tk.type] || "#06B6D4")} />
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
}
